import AlarmResource from '@/api/resources/alarm'
import type { ICreationAlarm, ICreationAlarmClassification } from '@/interfaces/api/alarm'
import type { IAlarmPages } from '@/interfaces/components/dataTableAlarms/alarm-pages'
import type { IAlarm, IClassification } from '@/interfaces/entities/alarm'
import { IAlarmReportMetrics } from '@/interfaces/stores/alarm'
import { AnalyticsEvents, logFirebaseAnalytics } from '@/utils/firebase'
import { defineStore } from 'pinia'
import { useErrorStore } from './error'
import { useLoadingStore } from './loading'
import { useSuccessStore } from './success'
import { IShift } from '@/interfaces/entities/shifts'
import { IGroup } from '@/interfaces/entities/group'

export interface IAlarmListingDynamicFilter {
  id: string
  field: string
  operator: string
  value: string | number | boolean
  valueType: 'string' | 'number' | 'date' | 'boolean'
}

export interface IAlarmListingTableState {
  page: number
  items: number
  sort: string
  sortDirection: 'asc' | 'desc'
  visibleColumns: string[]
  columnWidths: Record<string, number>
  dynamicFilters: IAlarmListingDynamicFilter[]
}

const DEFAULT_VISIBLE_COLUMNS = ['id', 'name', 'company', 'sensor', 'group', 'start_date', 'end_date', 'shift', 'classification_type']

function defaultListingTableState(): IAlarmListingTableState {
  return {
    page: 1,
    items: 10,
    sort: 'id',
    sortDirection: 'desc',
    visibleColumns: [...DEFAULT_VISIBLE_COLUMNS],
    columnWidths: {},
    dynamicFilters: [],
  }
}

export const useAlarmStore = defineStore({
  id: 'alarm',
  persist: {
    paths: [
      'listingTableState',
      'lastSearchAlarmScope',
      'currentClassificationStatusFilter',
      'lastSearchType',
      'lastPeriodReport',
      'lastPeriodClassification',
      'reportSensorIds',
      'reportAlarmTypeKeys',
      'reportClassificationTypeIds',
      'currentShiftFilter',
      'currentGroupFilter',
      'classifications',
    ],
  },

  state: () => ({
    alarms: [] as IAlarm[],
    classifications: [] as IClassification[],
    metrics: {} as IAlarmReportMetrics,
    baseMetrics: {} as IAlarmReportMetrics,
    compareMetrics: {} as IAlarmReportMetrics,
    reportSensorIds: [] as number[],
    reportAlarmTypeKeys: [] as string[],
    reportClassificationTypeIds: [] as number[],
    currentShiftFilter: null as IShift | null,
    currentGroupFilter: null as IGroup | null,
    currentClassificationStatusFilter: null as boolean | null,
    lastSearchType: null,
    lastSearchAlarmScope: 'individual' as 'group' | 'individual',
    lastPeriodReport: [] as Date[],
    lastPeriodClassification: [] as Date[],
    pagination: null as IAlarmPages['pagy'] | null,
    listingTableState: defaultListingTableState() as IAlarmListingTableState,
    openAlarms: [] as IAlarm[],
    openAlarmsLoading: false,
    openAlarmsError: false,
  }),

  getters: {
    listClassifications: (state) => state.classifications,
    getAlarmReportMetrics: (state) => state.metrics,
    getBaseAlarmReportMetrics: (state) => state.baseMetrics,
    getCompareAlarmReportMetrics: (state) => state.compareMetrics,
    getReportSensorIds: (state) => state.reportSensorIds,
    getReportAlarmTypeKeys: (state) => state.reportAlarmTypeKeys,
    getReportClassificationTypeIds: (state) => state.reportClassificationTypeIds,
    getCurrentShiftFilter: (state) => state.currentShiftFilter,
    getCurrentGroupFilter: (state) => state.currentGroupFilter,
    hasAlarmReportMetrics: (state) => state.metrics.total_alarms > 0,
    hasBaseAlarmReportMetrics: (state) => state.baseMetrics.total_alarms > 0,
    hasCompareAlarmReportMetrics: (state) => state.compareMetrics.total_alarms > 0,
    listAlarms: (state) => state.alarms,
    getLastPeriodReportFilter: (state) => state.lastPeriodReport,
    getLastPeriodClassificationFilter: (state) => state.lastPeriodClassification,
    getLastSearchAlarmsType: (state) => state.lastSearchType,
    getLastSearchAlarmScope: (state) => state.lastSearchAlarmScope,
    getPagination: (state) => state.pagination,
    getListingTableState: (state) => state.listingTableState,
    listOpenAlarms: (state) => state.openAlarms,
  },

  actions: {
    setLastSearchAlarmsType(type: string) {
      this.lastSearchType = type
    },

    setLastSearchAlarmScope(scope: 'individual' | 'group') {
      this.lastSearchAlarmScope = scope
    },

    async createAlarm(newAlarm: ICreationAlarm) {
      try {
        const createdAlarm = await AlarmResource.createAlarm(newAlarm)
        this.alarms.push(createdAlarm)
        return createdAlarm
      } catch {
        // Erro ao criar alarme
      }
    },

    async createClassification(newClassification: ICreationAlarmClassification) {
      try {
        const createdClassification = await AlarmResource.createClassification(newClassification)
        this.classifications.push(createdClassification)
        logFirebaseAnalytics(AnalyticsEvents.ALARM.CLASSIFICATION.CREATED)
      } catch {
        // Erro ao criar classificação
      }
    },

    async removeAlarm(id: number) {
      try {
        await AlarmResource.removeAlarm(id)
        this.classifications = this.classifications.filter((classification) => classification.id !== id)
        logFirebaseAnalytics(AnalyticsEvents.ALARM.CLASSIFICATION.REMOVED)
      } catch {
        // Erro ao remover classificação
      }
    },

    async getAlarms(start_date: string, end_date: string): Promise<IAlarm[]> {
      try {
        const response = await AlarmResource.getAlarms({ start_date, end_date })
        const alarms = response.data
        this.alarms = alarms
        return alarms
      } catch {
        return []
      }
    },

    async getAlarmsPages(
      page: number,
      items: number,
      sort: string,
      sort_direction: string,
      classified: boolean | null,
      filters: IAlarmListingDynamicFilter[] = [],
    ) {
      const loadingStore = useLoadingStore()
      const errorStore = useErrorStore()
      try {
        loadingStore.initLoading()
        errorStore.clearErrors()

        const classifiedParam = classified === null ? '' : classified
        const isGroupParam = this.lastSearchAlarmScope === 'group' ? true : false

        const result = await AlarmResource.getAlarmsPages(page, items, sort, sort_direction, classifiedParam, isGroupParam, filters)

        const data = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : []
        this.alarms = data

        if (result?.pagy) {
          this.pagination = result.pagy
        } else {
          const hasMorePages = data.length === items
          this.pagination = {
            count: (page - 1) * items + data.length,
            page,
            pages: hasMorePages ? page + 1 : Math.max(page, 1),
            prev: page > 1 ? page - 1 : null,
            next: hasMorePages ? page + 1 : null,
            items,
          }
        }
      } catch (error) {
        errorStore.setErrors('Falha ao classificar alarme!', error.message)
      } finally {
        loadingStore.removeLoading()
      }
    },

    async editAlarm(alarmId: number, classification_type_id: number, endAlarm = false) {
      try {
        const payload: { classification_type_id: number; end_alarm?: 'true' } = { classification_type_id }
        if (endAlarm) {
          payload.end_alarm = 'true'
        }
        const updatedAlarm = await AlarmResource.editAlarm(alarmId, payload)
        const index = this.alarms.findIndex((alarm) => alarm.id === alarmId)
        if (index !== -1) {
          this.alarms[index] = updatedAlarm
        }
        useSuccessStore().setSuccess(['Alarme classificado com sucesso!'])
        return updatedAlarm
      } catch (error) {
        useErrorStore().setErrors('Falha ao classificar alarme!', error.message)
      }
    },

    async fetchAlarmClassifications({ force = false } = {}) {
      if (this.classifications.length === 0 || force) {
        try {
          const classifications = await AlarmResource.getClassifications()
          this.classifications = classifications
          logFirebaseAnalytics(AnalyticsEvents.ALARM.CLASSIFICATION.LIST)
        } catch {
          // Erro ao buscar classificações
        }
      }
    },

    setLastPeriodReport(period: Date[]) {
      this.lastPeriodReport = period
    },

    setLastPeriodClassification(period: Date[]) {
      this.lastPeriodClassification = period
    },

    setReportSensorIds(ids: number[]) {
      this.reportSensorIds = ids
    },

    setReportAlarmTypeKeys(keys: string[]) {
      this.reportAlarmTypeKeys = keys
    },

    setReportClassificationTypeIds(ids: number[]) {
      this.reportClassificationTypeIds = ids
    },

    setCurrentShiftFilter(shift: IShift | null) {
      this.currentShiftFilter = shift
    },

    setCurrentGroupFilter(group: IGroup | null) {
      this.currentGroupFilter = group
    },

    setCurrentClassificationStatusFilter(status: boolean | null) {
      this.currentClassificationStatusFilter = status
    },

    setListingTableState(partial: Partial<IAlarmListingTableState>) {
      this.listingTableState = {
        ...this.listingTableState,
        ...partial,
      }
    },

    resetListingTableState() {
      this.listingTableState = defaultListingTableState()
    },

    async fetchOpenAlarms({ silent = false } = {}): Promise<IAlarm[]> {
      if (!silent) {
        this.openAlarmsLoading = true
      }
      try {
        const response = await AlarmResource.getOpenAlarms()
        const alarms = (response?.data ?? response ?? []) as IAlarm[]
        this.openAlarms = Array.isArray(alarms) ? alarms : []
        this.openAlarmsError = false
        return this.openAlarms
      } catch {
        if (!silent) {
          this.openAlarms = []
        }
        this.openAlarmsError = true
        return this.openAlarms
      } finally {
        if (!silent) {
          this.openAlarmsLoading = false
        }
      }
    },

    async fetchAlarmReportMetrics(dateRange: string) {
      try {
        const isClassification = this.lastSearchType === 'classified'
        const shiftId = this.currentShiftFilter?.id ?? ''
        const groupId = this.currentGroupFilter?.id ?? ''
        const classificationIds = isClassification ? this.reportClassificationTypeIds : []
        const response = await AlarmResource.fetchMetrics(
          dateRange,
          isClassification,
          this.reportSensorIds,
          this.reportAlarmTypeKeys,
          classificationIds,
          shiftId,
          groupId,
        )
        this.metrics = response
      } catch {
        // Erro ao buscar métricas
      }
    },
    async fetchBaseAlarmReportMetrics(dateRange: string) {
      try {
        const isClassification = this.lastSearchType === 'classified'
        const shiftId = this.currentShiftFilter?.id ?? ''
        const groupId = this.currentGroupFilter?.id ?? ''
        const classificationIds = isClassification ? this.reportClassificationTypeIds : []
        const response = await AlarmResource.fetchMetrics(
          dateRange,
          isClassification,
          this.reportSensorIds,
          this.reportAlarmTypeKeys,
          classificationIds,
          shiftId,
          groupId,
        )
        this.baseMetrics = response
      } catch {
        // Erro ao buscar métricas base
      }
    },
    async fetchCompareAlarmReportMetrics(dateRange: string) {
      try {
        const isClassification = this.lastSearchType === 'classified'
        const shiftId = this.currentShiftFilter?.id ?? ''
        const groupId = this.currentGroupFilter?.id ?? ''
        const classificationIds = isClassification ? this.reportClassificationTypeIds : []
        const response = await AlarmResource.fetchMetrics(
          dateRange,
          isClassification,
          this.reportSensorIds,
          this.reportAlarmTypeKeys,
          classificationIds,
          shiftId,
          groupId,
        )
        this.compareMetrics = response
      } catch {
        // Erro ao buscar métricas de comparação
      }
    },
  },
})
